import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import hubDeliveryService from "services/hubDeliveryService";

// List HubDelivery
export const useListHubDelivery = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["hub"]);
  const { addToast } = useToasts();

  const { params, enabled, queryKey } = payload;

  return useQuery(
    [queryKey || "hubDeliveries", params?.page, params?.columnFilter],
    () => hubDeliveryService.listHubDelivery(payload),
    {
      enabled: enabled || false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("hubDelivery"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

// List HubContact
export const useListHubContact = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["hubContact"]);
  const { addToast } = useToasts();

  const { params, enabled, queryKey } = payload;

  return useQuery(
    [queryKey || "hubContacts", params?.page],
    () => hubDeliveryService.listHubContact(payload),
    {
      enabled: enabled || false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("hubContacts"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

// Create HubDelivery
export const useCreateHubDelivery = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["hub"]);
  const { addToast } = useToasts();

  return useMutation(hubDeliveryService.createHubDelivery, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("hubDelivery"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("hubDelivery"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Detail HubDelivery
export const useDetailHubDelivery = (payload, onSuccess, onError) => {
  const { t, i18n } = useTranslation(["hub"]);
  const { addToast } = useToasts();

  return useQuery(
    ["hubDelivery", "detail"],
    () => hubDeliveryService.detailHubDelivery(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.detail", {
                target: t("hubDelivery"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Update HubDelivery
export const useUpdateHubDelivery = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["hub"]);
  const { addToast } = useToasts();

  return useMutation(hubDeliveryService.updateHubDelivery, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("hubDelivery"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("hubDelivery"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Active HubDelivery
export const useActiveHubDelivery = (onSuccess, onError) => {
  const { t } = useTranslation(["hub"]);
  const { addToast } = useToasts();

  return useMutation(hubDeliveryService.updateHubDelivery, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.active", {
          target: t("hubDelivery"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.active", {
              target: t("hubDelivery"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete HubDelivery
export const useInactiveHubDelivery = (onSuccess, onError) => {
  const { t } = useTranslation(["hub"]);
  const { addToast } = useToasts();

  return useMutation(hubDeliveryService.updateHubDelivery, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.delete", {
          target: t("hubDelivery"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("hubDelivery"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};
